import { Delete, Description, Warning } from "@mui/icons-material";
import { TableCell, useMediaQuery, useTheme } from "@mui/material";
import { useConsentContext } from "../../../contexts/ConsentContext";
import { useNavigate } from "react-router-dom";
import { GetBinary } from "../../../services/fhir/patientsummary/Binary/GetBinary";

import { isAndroid, isMobile, isMobileSafari } from "react-device-detect";
import { useState } from "react";
import { IframeDialog } from "../../ehrrecords/components/IframeDialog";
import {
  TableActionButton,
  TableActionGroup,
} from "../../../components/GenericTable/TableActionButton";

function ConsentActionsBar({
  row,
  type,
  fullWidth,
  compact,
  onOpenRecords,
  onViewDocument,
  onRevokeOrRenew,
}: {
  row: any;
  type: any;
  fullWidth: boolean;
  compact: boolean;
  onOpenRecords: () => void;
  onViewDocument: () => void;
  onRevokeOrRenew: () => void;
}) {
  if (type === "received") {
    if (!row?.isValid) return null;
    return (
      <TableActionGroup fullWidth={fullWidth}>
        <TableActionButton
          compact={compact}
          icon={<Description />}
          label="Prontuário"
          tone="primary"
          onClick={onOpenRecords}
        />
      </TableActionGroup>
    );
  }

  return (
    <TableActionGroup fullWidth={fullWidth}>
      {row?.binaryUrl ? (
        <TableActionButton
          compact={compact}
          icon={<Description />}
          label="Documento"
          tone="neutral"
          onClick={onViewDocument}
        />
      ) : null}
      <TableActionButton
        compact={compact}
        icon={
          row.vigency ? (
            row.isValid ? (
              <Delete />
            ) : (
              <Warning />
            )
          ) : (
            <Delete />
          )
        }
        label={row.isValid ? "Revogar" : "Renovar"}
        tone={row.isValid ? "error" : "neutral"}
        onClick={onRevokeOrRenew}
      />
    </TableActionGroup>
  );
}

export function ConsentActions({ row, type }: { row: any; type: any }) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));
  const {
    setRevokeDialog,
    setSubjectName,
    setSubjectId,
    setRenewDialog,
    setUUID,
  } = useConsentContext();
  const navigate = useNavigate();

  const [binaryUrl, setBinaryUrl] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  const handleClose = () => {
    setBinaryUrl("");
    setOpen(false);
  };

  const base64ToBlob = (base64: any, contentType = "") => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  async function downloadBase64File() {
    try {
      const response = await GetBinary({
        urlBinary: row?.binaryUrl,
      });
      const blob = base64ToBlob(response?.data, response?.contentType);
      const fileUrl = URL.createObjectURL(blob);
      const auxFile = {
        fileUrl,
        fileName: "Documento.pdf",
      };

      if (isMobileSafari && !isAndroid) {
        if (auxFile) {
          const link = document.createElement("a");
          link.href = auxFile?.fileUrl;
          link.download = auxFile?.fileName;
          link.click();
        } else {
          alert(
            "O arquivo ainda está sendo preparado, por favor tente novamente.",
          );
        }
      } else {
        window.open(auxFile.fileUrl, "_blank");
        handleClose();
      }
    } catch (err) {
      console.log("err", err);
    }
  }

  const actionBar = (
    <ConsentActionsBar
      row={row}
      type={type}
      fullWidth={isMobileLayout}
      compact={!isMobileLayout}
      onOpenRecords={() => {
        if (row.identifier)
          navigate("/summary", {
            state: {
              patientId: row?.identifier,
              purpose: row?.motivo,
            },
          });
      }}
      onViewDocument={() => {
        if (isMobile) void downloadBase64File();
        else {
          setBinaryUrl(row?.binaryUrl);
          setOpen(true);
        }
      }}
      onRevokeOrRenew={() => {
        setSubjectId(row?.identifier);
        setSubjectName(row?.name);
        setUUID(row?.subject);
        if ((row.isValid && row.vigency) || !row.vigency) setRevokeDialog(true);
        else setRenewDialog(true);
      }}
    />
  );

  const dialogs =
    open && binaryUrl ? (
      <IframeDialog handleClose={handleClose} isOpen={open} data={binaryUrl} />
    ) : null;

  if (isMobileLayout) {
    return (
      <>
        {actionBar}
        {dialogs}
      </>
    );
  }

  return (
    <>
      <TableCell align="center" sx={{ verticalAlign: "middle" }}>
        {actionBar}
      </TableCell>
      {dialogs}
    </>
  );
}
